import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { mdiFaceAgent } from '@mdi/js';
import Icon from '@mdi/react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import { Button } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsersStore } from 'services/users';
import CardHeader from './components/CardHeader';

const MonitoringCard = ({ data, onSelectSpecialists, loading }: any) => {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const [countdown, setCountdown] = useState<string>('');
	const [isExpired, setIsExpired] = useState<boolean>(false);

	// Calculate countdown based on the activation time
	useEffect(() => {
		if (!data?.request_activation_time) return;

		const calculateCountdown = () => {
			// Parse the activation time from the ISO string
			const activationTime = moment(data.request_activation_time);

			// Calculate the difference between now and activation time
			const now = moment();
			const diff = activationTime.diff(now);

			if (diff <= 0) {
				setCountdown(t('expired'));
				setIsExpired(true);
				return;
			}

			// Format the countdown
			const hours = Math.floor(diff / (1000 * 60 * 60));
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
			const seconds = Math.floor((diff % (1000 * 60)) / 1000);

			setCountdown(
				`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
			);
			setIsExpired(false);
		};

		// Initial calculation
		calculateCountdown();

		// Update every second
		const interval = setInterval(calculateCountdown, 1000);

		// Cleanup interval on component unmount
		return () => clearInterval(interval);
	}, [data?.request_activation_time]);

	const renderStatusMessage = () => {
		switch (data.payment_status) {
			case 'invoice_review':
				return (
					<p className="text-custom-yellow flex items-center">
						<HistoryToggleOffIcon />
						<span className="ml-1">{t('requestProcessing')}</span>
					</p>
				);

			case 'invoice_sent':
				return (
					<p className="text-primary text-center">
						<CheckCircleOutlineIcon />
						<span className="ml-1">{t('invoiceHasBeenSentTo')}</span>
						{/* <br />
						<span className="text-dark font-bold">{'test@gmail.com'}</span> */}
					</p>
				);
		}

		return null;
	};

	const getPaymentInfo = () => {
		if (data.payment_status === 'invoice_review') return t('paymentProcessing');
		if (data.payment_status === 'invoice_sent') return t('completePayment');
	};

	const { user } = useUsersStore();

	const userRole = user?.role?.toLowerCase();

	return (
		<div className="border rounded-lg shadow-md w-full bg-white relative">
			<CardHeader
				price={data.services?.price}
				phases={data.services?.phases}
				payVia={data.pay_via}
				title={data.crop?.name}
				isCompleted={data.isCompleted}
				monitoringType={data.monitoringType}
				invoice_url={data.invoice_url}
				image={data.crop?.image_url}
			/>
			<div className="mt-3 py-2 flex justify-between bg-disabled border-y border-light px-5">
				<span className="text-light-grey text-sm">
					{t('duration')}: {data?.services?.duration} {t('years')}
				</span>
				<span className="text-dark font-normal text-sm border-l border-light pl-1">
					{moment(data?.end_date).format('DD.MM.YYYY')}
				</span>
			</div>

			{data.payment_status !== 'invoice_payed' && renderStatusMessage() && (
				<div className="py-3 flex flex-col items-center text-sm font-bold text-center">
					{renderStatusMessage()}
				</div>
			)}

			{data.payment_status === 'invoice_payed' || data.payment_status === 'stripe_payed' ? (
				<div className="flex flex-col items-center p-4">
					{!isExpired && data.request_activation_time && (
						<span className="text-sm text-[#FD3333]">
							{t('availableWithin', { hours: countdown })}
						</span>
					)}

					{!data?.selected_specialist_id ? (
						<Button
							variant="contained"
							fullWidth
							loading={loading === data?.id}
							disabled={loading === data?.id || !isExpired}
							onClick={() => onSelectSpecialists()}
							sx={{
								backgroundColor: !isExpired ? '#FFAA2B' : '#00AB89',
								color: 'white',
								mt: 1,
								'@media (max-width: 400px)': {
									fontSize: '12px',
								},
								'&:disabled': {
									backgroundColor: !isExpired ? '#FFAA2B' : '#00AB89',
								},
							}}
						>
							{t('selectSpecialist')}
						</Button>
					) : (
						<Button
							variant="contained"
							fullWidth
							onClick={() => navigateWithLanguage('/farmer/monitoring/ongoing-projects')}
							sx={{
								backgroundColor: '#00AB89',
								color: 'white',
								mt: 1,
								'@media (max-width: 400px)': {
									fontSize: '12px',
								},
							}}
						>
							{t('requestsAndOffers.ongoingProjects')}
						</Button>
					)}
				</div>
			) : (
				<div className="flex w-full gap-10 p-4 justify-between items-center max-xsm:flex-col">
					<p className="text-error text-sm">{getPaymentInfo()}</p>
					<Button
						variant="contained"
						disabled={!data?.channel}
						onClick={() =>
							navigateWithLanguage(`/${userRole}/chats?channelId=${data?.channel?.id}`)
						}
						sx={{
							backgroundColor: 'white',
							color: 'black',
							minWidth: '120px',
							width: '100%',
							'@media (max-width: 400px)': {
								fontSize: '12px',
							},
						}}
						startIcon={<Icon path={mdiFaceAgent} size={1} />}
					>
						{t('support')}
					</Button>
				</div>
			)}
		</div>
	);
};

export default MonitoringCard;
